import { ArrowBigLeft, TriangleAlertIcon } from 'lucide-react';
import { ReactElement, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Icon, Inline, Stack, TableCell, withTooltip } from '@crossnokaye/ux-common';

import { DetailsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';

type DirtyEventCellProps = {
  index: number;
  initialEvent?: DetailsFormData['defrostSchedule'][number];
};

const IconWithTooltip = withTooltip(Icon);

export const FormattedEvent = ({
  event,
  className,
}: {
  event: DetailsFormData['defrostSchedule'][number];
  className: string;
}): ReactElement => {
  return (
    <Stack gap="none" className={className}>
      <Inline gap="xs">
        <span className="font-medium">EventDays:</span>
        <span>{event.eventDays?.join(', ')}</span>
      </Inline>
      <Inline gap="xs">
        <span className="font-medium">Start Time:</span>
        <span>{event.startTime?.toLocaleTimeString()}</span>
      </Inline>
      {event.endDate && (
        <Inline gap="xs">
          <span className="font-medium">End Date:</span>
          <span>{event.endDate?.toLocaleDateString()}</span>
        </Inline>
      )}
    </Stack>
  );
};

const TooltipContent = ({
  currentEvent,
  initialEvent,
}: {
  currentEvent: DetailsFormData['defrostSchedule'][number];
  initialEvent?: DetailsFormData['defrostSchedule'][number];
}): ReactElement => {
  return (
    <Inline
      gap="none"
      className="m-0 flex items-center justify-center gap-0.5 whitespace-nowrap text-ckds-input-fg-new"
    >
      <FormattedEvent event={currentEvent} className={'text-ckds-input-fg-new'} />
      <ArrowBigLeft />
      {initialEvent && <FormattedEvent event={initialEvent} className={'text-muted-foreground'} />}
    </Inline>
  );
};

export const DirtyEventCell: React.FC<DirtyEventCellProps> = ({ index, initialEvent }) => {
  const form = useFormContext();
  // Use watch() instead of getValues() to get default values for unregistered fields
  const data = form.watch();

  const isDirty = useMemo(() => {
    if (!initialEvent) return true;
    const currentEvent = data?.defrostSchedule?.[index];

    if (!currentEvent) return true;

    return (
      currentEvent.startTime?.getTime() !== initialEvent.startTime?.getTime() ||
      JSON.stringify(currentEvent.eventDays) !== JSON.stringify(initialEvent.eventDays) ||
      currentEvent.endDate?.getTime() !== initialEvent.endDate?.getTime()
    );
  }, [data, initialEvent, index]);

  return (
    <TableCell>
      {isDirty && (
        <span data-testid="dirty-event-indicator">
          <IconWithTooltip
            delayDuration={0}
            icon={TriangleAlertIcon}
            color="text-ckds-base-caution"
            tooltipContent={
              isDirty ? (
                <TooltipContent
                  currentEvent={data?.defrostSchedule?.[index]}
                  initialEvent={initialEvent}
                />
              ) : undefined
            }
            contentClassName="text-ckds-base-caution"
          />
        </span>
      )}
    </TableCell>
  );
};
